import bbox from '@turf/bbox';
import { cellToBoundary } from 'h3-js';
import { InsightServiceApi } from 'lib/apis/services/insight';
import { useMarketInsightMapBoundaryQuery } from 'lib/apis/services/insight/service';
import { generateHeatmapColor } from 'lib/utils/helpers/generateHeatMapColor';
import {
  EventData,
  Map,
  MapMouseEvent,
  MapboxEvent,
  MapboxGeoJSONFeature,
} from 'mapbox-gl';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { MapRef, ViewStateChangeEvent } from 'react-map-gl';

type PopupLocation = { lat?: number; lng?: number; price?: number };

interface H3MapReducer {
  viewPortBounding: Array<Array<number[]>>;
  zoomLevel: number;
  popup?: PopupLocation;
}

type Coordinates = Array<Array<number[]>>;
type FeaturePropertiesType = {
  value: number;
  color: string;
  opacity: number;
  id: string;
};

function mapH3Reducer(
  state: H3MapReducer,
  action: {
    type: 'setBoundingBox' | 'setZoomLevel' | 'setPopuplocation';
    payload: Coordinates | H3MapReducer | PopupLocation;
  },
): H3MapReducer {
  switch (action.type) {
    case 'setBoundingBox':
      return {
        ...state,
        viewPortBounding: action.payload as Coordinates,
      };

    case 'setZoomLevel':
      return {
        ...state,
        zoomLevel: (action.payload as H3MapReducer).zoomLevel,
        viewPortBounding: (action.payload as H3MapReducer).viewPortBounding,
      };

    case 'setPopuplocation':
      return {
        ...state,
        popup: {
          ...state.popup,
          ...(action.payload as PopupLocation),
        },
      };
    default:
      throw new Error('Set type.');
  }
}

const getBoundingBox = (map: Map | MapRef) => {
  const canvas = map.getCanvas(),
    w = canvas.width,
    h = canvas.height,
    cUL = map.unproject([0, 0]).toArray(),
    cUR = map.unproject([w, 0]).toArray(),
    cLR = map.unproject([w, h]).toArray(),
    cLL = map.unproject([0, h]).toArray();
  return [cUL, cUR, cLR, cLL, cUL];
};

export const useHpiMapModal = (locationId: string, locationType: string) => {

  const { data } = useMarketInsightMapBoundaryQuery(locationId, locationType);

  const [states, setState] = useReducer(mapH3Reducer, {
    viewPortBounding: [],
    zoomLevel: 9,
    popup: {},
  });
  const { data: h3Data } = InsightServiceApi.useH3Query({
    boundingBox: { coordinates: states.viewPortBounding },
    zoomLevel: states.zoomLevel,
  });
  const mapRef = useRef<MapRef>(null);
  const [showpopup, setShowpopup] = useState(false);

  const mapEventHandler = useCallback(
    (e: ViewStateChangeEvent, type: 'zoom' | 'drag' | 'rotate' = 'drag') => {
      const coordinates = getBoundingBox(e.target);

      if (type === 'drag') {
        setState({ type: 'setBoundingBox', payload: [coordinates] });
      }
      if (type === 'zoom') {
        setState({
          type: 'setZoomLevel',
          payload: {
            viewPortBounding: [coordinates],
            zoomLevel: Math.round(e.target.getZoom()),
          },
        });
      }
    },
    [JSON.stringify(h3Data)],
  );

  const processH3Cells = useMemo(() => {
    return h3Data?.cells.map((item) => {
      const color = generateHeatmapColor(
        item.content.value,
        h3Data.minValue,
        h3Data.maxValue,
        0.5,
      );
      return {
        type: 'Feature',
        properties: {
          value: item.content.value,
          color: color,
          opacity: 1,
          id: item.h3id,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [cellToBoundary(item.h3id, true)],
        },
      };
    });
  }, [h3Data]);

  const onMouseMove = (
    e: MapMouseEvent & {
      features?: MapboxGeoJSONFeature[] | undefined;
    } & EventData,
  ) => {
    const property =
      e.features && (e.features[0].properties as FeaturePropertiesType);

    setState({
      type: 'setPopuplocation',
      payload: {
        lat: e.lngLat.lat,
        lng: e.lngLat.lng,
        price: property && property.value,
      },
    });
    setShowpopup(true);
  };

  const onMouseLeave = () => {
    setShowpopup(false);
  };

  const mapLoadHandler = useCallback(
    (e: MapboxEvent) => {
      const coordinates = getBoundingBox(e.target);
      setState({ type: 'setBoundingBox', payload: [coordinates] });
    },
    [JSON.stringify(h3Data)],
  );

  // useEffect(() => {
  //   if (data) {
  //     const [minLng, minLat, maxLng, maxLat] = bbox(data?.coors);
  //     setTimeout(() => {
  //       if (mapRef.current) {
  //         mapRef.current.fitBounds(
  //           [
  //             [minLng, minLat],
  //             [maxLng, maxLat],
  //           ],
  //           { padding: 40, duration: 1000 },
  //         );
  //       }
  //     }, 1000);
  //   }
  // }, [data]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on('mousemove', 'h3DataLayer', onMouseMove);
      mapRef.current.on('mouseleave', 'h3DataLayer', onMouseLeave);
      return () => {
        if (mapRef.current) {
          mapRef.current.off('mousemove', 'h3DataLayer', onMouseMove);
          mapRef.current.off('mouseleave', 'h3DataLayer', onMouseLeave);
        }
      };
    }
  }, [mapRef.current]);

  return {
    mapLoadHandler,
    mapEventHandler,
    mapRef,
    data,
    states,
    h3Data,
    showpopup,
    setShowpopup,
    processH3Cells,
  };
};
